/**
 * Ladders (บันไดสวรรค์ / บันไดนรก): ranked one-on-one duels in the arena. Queue, get matched with a
 * contender near your rating, fight; a win raises the rating and a loss lowers it (Elo). Every bout
 * pays ladder points for the ladder's shop, and each rank tier reached pays a reward once a season.
 * The heavenly ladder is for the righteous and the unaligned; the hellish one for evil names.
 */
import type { Karma } from '../karma';
import type { PassReward } from './pass';

export type LadderId = 'heaven' | 'hell';
export const LADDER_IDS: readonly LadderId[] = ['heaven', 'hell'];

export interface LadderTier {
  readonly name: string;
  /** Rating needed. */
  readonly rating: number;
  readonly reward: PassReward;
}

/** Someone on the ladder (simulated until real players arrive online). */
export interface Contender {
  readonly name: string;
  readonly title: string;
  readonly sprite: string;
  /** Martial art they fight with. */
  readonly art: string;
  readonly rating: number;
}

export type LadderWare =
  | { readonly kind: 'item'; readonly id: string; readonly price: number }
  | { readonly kind: 'reward'; readonly name: string; readonly reward: PassReward; readonly price: number };

export interface LadderDef {
  readonly id: LadderId;
  readonly name: string;
  readonly glyph: string;
  /** Who may climb it. */
  readonly side: string;
  readonly allows: (karma: Karma) => boolean;
  /** What its points are called. */
  readonly points: string;
  readonly tiers: readonly LadderTier[];
  readonly shop: readonly LadderWare[];
  readonly contenders: readonly Contender[];
}

export const LADDER = {
  season: 's1',
  startRating: 1000,
  /** Elo K-factor. */
  k: 32,
  /** Seconds spent looking for an opponent (min, extra random). */
  queue: [3, 3] as const,
  countdown: 3,
  fightSeconds: 90,
  /** Seconds the result is shown before leaving the arena. */
  resultSeconds: 3,
  /** Ladder points for taking part. */
  points: { win: 30, loss: 10 },
  /** Wins that pay ladder points per day (the arena's day); more wins still move the rating (ECONOMY E1). */
  dailyPointWins: 10,
  /** How far from the arena's centre fighters may move. */
  arenaRadius: 4.6,
} as const;

/** Rating change for a bout against `opp` (Elo). */
export function eloDelta(rating: number, opp: number, won: boolean): number {
  const expected = 1 / (1 + 10 ** ((opp - rating) / 400));
  return Math.round(LADDER.k * ((won ? 1 : 0) - expected));
}

/** How much stronger than the player a contender of `opp` rating is matched to be (0.85–1.25). */
export const contenderStrength = (rating: number, opp: number): number =>
  Math.min(1.25, Math.max(0.85, 1 + (opp - rating) / 1200));

/** The ladder a karma may climb. */
export const ladderFor = (karma: Karma): LadderId =>
  karma === 'evil' || karma === 'mad' ? 'hell' : 'heaven';

/** Index of the highest tier a rating has reached (-1 below the first). */
export function tierAt(def: LadderDef, rating: number): number {
  let k = -1;
  def.tiers.forEach((t, i) => {
    if (rating >= t.rating) k = i;
  });
  return k;
}

const SPRITES = [
  'heroWudang',
  'red',
  'night',
  'lilac',
  'taoist',
  'monk',
  'guardOfficer',
  'blissLady',
  'scholar',
];
const ARTS = [
  'breeze-sword',
  'drift-fist',
  'drift-sword',
  'snow-sword',
  'bell-fist',
  'cloud-palm',
  'ink-slate',
];

/** A ladder's field of contenders, spread from `low` to `high` rating. */
function field(
  names: readonly string[],
  titles: readonly string[],
  low: number,
  high: number,
  sprites = SPRITES,
) {
  return names.map((name, i): Contender => ({
    name,
    title: titles[i % titles.length]!,
    sprite: sprites[i % sprites.length]!,
    art: ARTS[(i * 3) % ARTS.length]!,
    rating: Math.round(high - ((high - low) * i) / (names.length - 1)),
  }));
}

const HEAVEN: LadderDef = {
  id: 'heaven',
  name: 'บันไดสวรรค์',
  glyph: '天',
  side: 'ฝ่ายธรรมะและผู้ไม่ฝักใฝ่ฝ่ายใด',
  allows: (k) => k !== 'evil' && k !== 'mad',
  points: 'แต้มสวรรค์',
  tiers: [
    { name: 'ขั้นศิลา', rating: 1050, reward: { gold: 2000 } },
    { name: 'ขั้นสัมฤทธิ์', rating: 1150, reward: { gold: 4000, cultivation: 500 } },
    { name: 'ขั้นเงิน', rating: 1300, reward: { jade: 100, cultivation: 1000 } },
    { name: 'ขั้นทอง', rating: 1450, reward: { jade: 200, gold: 10000 } },
    { name: 'ขั้นหยก', rating: 1600, reward: { jade: 400, skin: 'azure-fox' } },
    { name: 'ขั้นเทพ', rating: 1800, reward: { jade: 800, title: 'heaven-ladder' } },
  ],
  shop: [
    { kind: 'item', id: 'heaven-sword', price: 600 },
    { kind: 'item', id: 'heaven-crown', price: 450 },
    { kind: 'reward', name: 'แต้มฝึกปรือ 800', reward: { cultivation: 800 }, price: 120 },
    { kind: 'reward', name: 'หยกเซียน 50', reward: { jade: 50 }, price: 200 },
    { kind: 'reward', name: '500 ตำลึง', reward: { gold: 500 }, price: 100 },
  ],
  contenders: field(
    [
      'กระบี่เมฆาขาว',
      'ฟ้าใสไร้เมฆ',
      'หลิวอวิ๋นเฟิง',
      'จันทร์กระจ่างฟ้า',
      'ศิษย์พี่ซ่ง',
      'เทพธิดาหิมะ',
      'คมกระบี่พิทักษ์ธรรม',
      'ลมวสันต์',
      'ดาบสายรุ้ง',
      'บุปผาแรกแย้ม',
      'นักพรตน้อย',
      'หมัดพยัคฆ์',
      'ศิษย์ใหม่หวัง',
      'เด็กหนุ่มถือไม้',
    ],
    ['ผู้พิทักษ์คุณธรรม', 'จอมยุทธ์พเนจร', 'ศิษย์สำนักยอดหมอก', 'ศิษย์หินระฆัง', 'บัณฑิตถือกระบี่'],
    880,
    1880,
  ),
};

const HELL: LadderDef = {
  id: 'hell',
  name: 'บันไดนรก',
  glyph: '獄',
  side: 'ฝ่ายมาร (กรรมชั่วหรือบ้า)',
  allows: (k) => k === 'evil' || k === 'mad',
  points: 'แต้มนรก',
  tiers: [
    { name: 'ขั้นผีเร่ร่อน', rating: 1050, reward: { gold: 2000 } },
    { name: 'ขั้นอสูร', rating: 1150, reward: { gold: 4000, cultivation: 500 } },
    { name: 'ขั้นยักษ์มาร', rating: 1300, reward: { jade: 100, cultivation: 1000 } },
    { name: 'ขั้นเจ้านรก', rating: 1450, reward: { jade: 200, gold: 10000 } },
    { name: 'ขั้นยมราช', rating: 1600, reward: { jade: 400, skin: 'violet-fan-swords' } },
    { name: 'ขั้นราชันมาร', rating: 1800, reward: { jade: 800, title: 'hell-ladder' } },
  ],
  shop: [
    { kind: 'item', id: 'hell-sabre', price: 600 },
    { kind: 'item', id: 'hell-coat', price: 450 },
    { kind: 'reward', name: 'แต้มฝึกปรือ 800', reward: { cultivation: 800 }, price: 120 },
    { kind: 'reward', name: 'หยกเซียน 50', reward: { jade: 50 }, price: 200 },
    { kind: 'reward', name: '500 ตำลึง', reward: { gold: 500 }, price: 100 },
  ],
  contenders: field(
    [
      'โลหิตพันลี้',
      'มารกระบี่ทมิฬ',
      'ผีเสื้อพิษ',
      'เงามัจจุราช',
      'ดาบกระหายเลือด',
      'นางมารเหมันต์',
      'อสูรหน้าหยก',
      'คมเขี้ยวหมาป่า',
      'มือสังหารไร้นาม',
      'ปีศาจหัวเราะ',
      'โจรเดียวดาย',
      'หมาป่าแดง',
      'ผีพเนจร',
      'มารน้อย',
    ],
    ['ผู้ทรยศสำนัก', 'มือสังหารรับจ้าง', 'ศิษย์ดาบชาด', 'จอมมารพเนจร', 'หัวหน้าโจร'],
    880,
    1880,
    ['bloodMaster', 'night', 'blissLady', 'red', 'guardOfficer', 'lilac'],
  ),
};

export const LADDERS: Readonly<Record<LadderId, LadderDef>> = { heaven: HEAVEN, hell: HELL };

/** Han glyphs the ladders put on the UI. */
export const LADDER_GLYPHS = '梯' + HEAVEN.glyph + HELL.glyph;
